import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { cartTotals, formatCAD, getCart, removeFromCart, updateQty } from "@/lib/utils-cart";
import { Trash2, ShoppingBag, ArrowRight } from "lucide-react";

export default function Cart() {
  const [cart, setCart] = useState(getCart());
  const t = cartTotals(cart);
  const nav = useNavigate();

  useEffect(() => {
    const sync = () => setCart(getCart());
    window.addEventListener("vendora-cart-changed", sync);
    return () => window.removeEventListener("vendora-cart-changed", sync);
  }, []);

  const setQ = (id, q) => { updateQty(id, q); setCart(getCart()); };
  const remove = (id) => { removeFromCart(id); setCart(getCart()); };

  if (cart.length === 0) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-6 py-32 text-center">
          <ShoppingBag size={48} className="mx-auto text-muted-foreground" />
          <h1 className="font-display text-3xl font-bold text-primary mt-6">Your cart is empty.</h1>
          <p className="mt-2 text-muted-foreground">Browse the marketplace and find products from vetted Canadian suppliers.</p>
          <Link to="/marketplace" className="v-btn-primary mt-6" data-testid="cart-empty-cta">Shop the marketplace</Link>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <h1 className="font-display text-3xl font-black tracking-tight text-primary">Your cart</h1>
          <div className="mt-6 space-y-3">
            {cart.map((item) => (
              <div key={item.product_id} className="v-card flex gap-4" data-testid={`cart-item-${item.product_id}`}>
                <img src={item.image} alt={item.title} className="w-24 h-24 object-cover bg-muted rounded-sm" />
                <div className="flex-1">
                  <div className="font-semibold text-primary">{item.title}</div>
                  <div className="mt-1 font-mono text-sm">{formatCAD(item.price)}</div>
                  <div className="mt-3 flex items-center gap-3">
                    <input type="number" min={1} value={item.quantity} onChange={(e) => setQ(item.product_id, Number(e.target.value))} className="v-input w-20" data-testid={`cart-qty-${item.product_id}`} />
                    <button onClick={() => remove(item.product_id)} className="text-destructive hover:bg-destructive/10 px-2 py-1 rounded-sm text-sm flex items-center gap-1" data-testid={`cart-remove-${item.product_id}`}>
                      <Trash2 size={14} /> Remove
                    </button>
                  </div>
                </div>
                <div className="font-display font-bold text-lg text-foreground">{formatCAD(item.price * item.quantity)}</div>
              </div>
            ))}
          </div>
        </div>

        <aside className="v-card h-fit lg:sticky lg:top-24">
          <h2 className="font-display text-xl font-bold text-primary">Order summary</h2>
          <div className="mt-4 space-y-2 text-sm">
            <Row label="Subtotal" value={formatCAD(t.subtotal)} />
            <Row label="Tax (HST 13%)" value={formatCAD(t.tax)} />
            <Row label="Shipping" value={t.shipping ? formatCAD(t.shipping) : "Free"} />
          </div>
          <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
            <span className="font-display font-bold text-primary">Total</span>
            <span className="font-display font-black text-2xl text-foreground">{formatCAD(t.total)}</span>
          </div>
          <button onClick={() => nav("/checkout")} className="v-btn-primary w-full mt-6" data-testid="cart-checkout">
            Checkout <ArrowRight size={16} />
          </button>
          <Link to="/marketplace" className="block mt-3 text-center text-sm text-muted-foreground hover:text-primary">Continue shopping</Link>
        </aside>
      </section>
    </PublicLayout>
  );
}

function Row({ label, value }) {
  return <div className="flex items-center justify-between"><span className="text-muted-foreground">{label}</span><span className="font-mono">{value}</span></div>;
}
